import { useState } from "react";
import { Button } from "./ui-common/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui-common/card";
import { Textarea } from "./ui-common/textarea";
import { Label } from "./ui-common/label";
import { Input } from "./ui-common/input";
import { ScrollArea } from "./ui-common/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui-common/tooltip";
import { ArrowLeft, Mail, Send, LogOut } from "lucide-react";
import axiosInstance from "./axiosInstance";

const SupportPage = ({ user, onBackToChat, onLogout }) => {
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Check if form is valid for button state
  const isFormValid = description.trim().length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!description.trim()) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await axiosInstance.post('/api/support/send-email', {
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        description: description.trim(),
      });

      if (response.data.status === 'success') {
        setSubmitted(true);
        // Clear form
        setDescription("");
        setName("");
        setEmail("");
      } else {
        setError(response.data.message || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting support request:', err);
      setError(err.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSupport = () => {
    const subject = encodeURIComponent("Study Buddy Support Request");
    const body = encodeURIComponent("Hello Study Buddy Support Team,\n\nI need assistance with:\n\n[Please describe your issue here]\n\nBest regards");
    window.location.href = `mailto:digitaltutor@mail.tau.ac.il?subject=${subject}&body=${body}`;
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Fixed Header */}
        <div className="sticky top-0 z-10 bg-muted border-b px-6 py-4 backdrop-blur-sm bg-muted/95">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onBackToChat}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Support & Feedback</h1>
                <p className="text-muted-foreground">
                  We're here to help improve your Study Buddy experience
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" onClick={onLogout}>
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Logout</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-6 pt-8">
          <Card>
            <CardHeader>
              <CardTitle>Support & Feedback</CardTitle>
              <CardDescription>
                One simple form for bugs, ideas, or general help
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <div className="text-center py-8">
                  <div className="mb-4">
                    <Mail className="h-12 w-12 mx-auto text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Thanks! We got your message. 🎉</h3>
                  <p className="text-muted-foreground">
                    We'll review it and get back to you as soon as we can.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSubmitted(false)}
                    className="mt-4"
                  >
                    Submit Another
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your Name <span className="text-muted-foreground text-xs">(optional)</span></Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Enter your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Your Email <span className="text-muted-foreground text-xs">(optional)</span></Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Your Feedback</Label>
                    <Textarea
                      id="description"
                      placeholder="Share your thoughts, report bugs, suggest ideas, or ask for help..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={6}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={!isFormValid || isSubmitting}
                    variant={isFormValid ? "default" : "secondary"}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {isSubmitting ? "Sending..." : "Send Feedback"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
              <CardDescription>
                Quick answers to common questions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-80">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">How do I access course materials?</h4>
                    <p className="text-sm text-muted-foreground">
                      Navigate to your course page and use the chat to ask questions about your course materials and assignments.
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">Can I search through past conversations?</h4>
                    <p className="text-sm text-muted-foreground">
                      Yes! Your conversation history is saved and you can switch between different conversations in the left panel.
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">How does the Study Buddy chat work?</h4>
                    <p className="text-sm text-muted-foreground">
                      The AI assistant can answer questions using course-specific knowledge. It provides sources and references from your course materials.
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">Who can see my conversations?</h4>
                    <p className="text-sm text-muted-foreground">
                      Your conversations are private and only visible to you and your course instructors for academic support purposes.
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">How do I save a conversation?</h4>
                    <p className="text-sm text-muted-foreground">
                      All conversations are automatically saved. You can access them anytime from the Conversations panel in your course.
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">How do I provide feedback on responses?</h4>
                    <p className="text-sm text-muted-foreground">
                      Each AI response has thumbs up/down buttons. Click them to rate the quality of responses and help improve the system.
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">Can I copy AI responses?</h4>
                    <p className="text-sm text-muted-foreground">
                      Yes! Each response has a copy button that allows you to copy the text to your clipboard for use elsewhere.
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">How do I switch between courses?</h4>
                    <p className="text-sm text-muted-foreground">
                      Use the back arrow to return to the courses page where you can select a different course to chat about.
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">Can I use Study Buddy on mobile?</h4>
                    <p className="text-sm text-muted-foreground">
                      Yes! Study Buddy is fully responsive and works great on mobile devices and tablets.
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">What browsers are supported?</h4>
                    <p className="text-sm text-muted-foreground">
                      Study Buddy works on all modern browsers including Chrome, Firefox, Safari, and Edge.
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">How do I delete conversations?</h4>
                    <p className="text-sm text-muted-foreground">
                      Hover over a conversation in the left panel and click the delete (trash) icon. Confirm to permanently delete it.
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">What are the system requirements?</h4>
                    <p className="text-sm text-muted-foreground">
                      Study Buddy requires a modern web browser and stable internet connection. No additional software needed.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default SupportPage;
